import { Chessboard, COLOR, BORDER_TYPE, FEN } from 'cm-chessboard'
import { Markers, MARKER_TYPE } from 'cm-chessboard/src/extensions/markers/Markers.js'

import { h } from './dom.js'
import { on } from '../lib/bus.js'
import { state, patchOverlay } from '../lib/state.js'
import { activeIndexAt } from '../lib/timeline.js'
import { splitGeometry } from '../lib/split.js'
import { BOARD_THEMES, PIECE_SETS, CM_ASSETS_URL } from '../config.js'
import { getContentBox, getStage, getFrameBox } from './player.js'

let stage = null
let wrap = null
let host = null
let board = null
let appliedIndex = null // move index currently shown; null forces a re-apply
let structural = { pieces: null, coords: null, orientation: null }

// cm-chessboard caches the piece sprite into a hidden div via async XHR, but
// <use href="#id"> refs created before the sprite lands never re-resolve in
// Chrome. Pre-populate the cache div ourselves before the board renders.
async function ensureSpriteCached() {
  const wrapperId = 'cm-chessboard-sprite'
  if (document.getElementById(wrapperId)) return
  const file = PIECE_SETS[state.overlay.pieces]?.file ?? 'standard.svg'
  const res = await fetch(`${CM_ASSETS_URL}pieces/${file}`)
  if (!res.ok) return // board still works; cm-chessboard will retry its own way
  const wrapper = document.createElement('div')
  wrapper.id = wrapperId
  wrapper.style.transform = 'scale(0)'
  wrapper.style.position = 'absolute'
  wrapper.setAttribute('aria-hidden', 'true')
  // Parse as real SVG. Inserting the file as HTML text fragments the sprite
  // (the HTML parser bails partway through), leaving <g id="wk"> nodes in the
  // HTML namespace where <use> references can't render them.
  const doc = new DOMParser().parseFromString(await res.text(), 'image/svg+xml')
  if (doc.documentElement?.nodeName !== 'svg') return
  wrapper.appendChild(document.importNode(doc.documentElement, true))
  document.body.appendChild(wrapper)
}

export async function mountOverlay() {
  stage = getStage()
  host = h('div.board-host')
  wrap = h(
    'div.board-overlay',
    {},
    h('div.drag-bar', { title: 'Drag to reposition' }, '⠿ drag board'),
    host,
    h('div.resize-handle', { title: 'Drag to resize' }),
  )
  stage.appendChild(wrap)

  applyTheme()
  layout()
  await ensureSpriteCached()
  createBoard()
  wireDrag()
  wireResize()

  new ResizeObserver(() => layout()).observe(stage)

  on('overlay', () => {
    applyTheme()
    layout()
    syncStructural()
  })
  on('video', () => layout())
  on('time', () => syncPosition())
  on('stamps', () => syncPosition(true))
  on('pgn', () => {
    appliedIndex = null
    syncPosition(true)
  })
}

function fenAt(index) {
  if (index < 0) return state.pgn.startFen ?? FEN.start
  return state.pgn.moves[index]?.after ?? FEN.start
}

function createBoard() {
  const o = state.overlay
  board?.destroy()
  board = new Chessboard(host, {
    position: fenAt(appliedIndex ?? -1),
    orientation: o.orientation === 'black' ? COLOR.black : COLOR.white,
    responsive: true,
    assetsUrl: CM_ASSETS_URL,
    // Cache sprites into the document (same-document <use href="#id"> refs).
    // External sprite-URL refs (assetsCache: false) don't paint reliably.
    assetsCache: true,
    style: {
      cssClass: 'pgnsync',
      showCoordinates: o.coords,
      borderType: BORDER_TYPE.none,
      pieces: { file: `pieces/${PIECE_SETS[o.pieces]?.file ?? 'standard.svg'}` },
      animationDuration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300,
    },
    extensions: [{ class: Markers, props: { autoMarkers: null } }],
  })
  structural = { pieces: o.pieces, coords: o.coords, orientation: o.orientation }
  markLastMove(appliedIndex ?? -1)
}

// Piece set / coordinates need a rebuild; orientation is animated in place.
async function syncStructural() {
  const o = state.overlay
  if (o.pieces !== structural.pieces || o.coords !== structural.coords) {
    if (o.pieces !== structural.pieces) {
      // Both piece sprites use the same symbol ids — swap the cached sprite
      // div so the new set actually renders (the cache is keyed by div id).
      document.getElementById('cm-chessboard-sprite')?.remove()
      await ensureSpriteCached()
    }
    createBoard()
    return
  }
  if (o.orientation !== structural.orientation) {
    structural.orientation = o.orientation
    board.setOrientation(o.orientation === 'black' ? COLOR.black : COLOR.white, true)
  }
}

function syncPosition(force = false) {
  if (!board) return
  const index = activeIndexAt(state.stamps, state.playback.time)
  if (!force && index === appliedIndex) return
  appliedIndex = index
  board.setPosition(fenAt(index), state.playback.playing)
  markLastMove(index)
  layout()
}

function markLastMove(index) {
  if (!board?.removeMarkers) return
  board.removeMarkers()
  const move = state.pgn.moves[index]
  if (move) {
    board.addMarker(MARKER_TYPE.square, move.from)
    board.addMarker(MARKER_TYPE.square, move.to)
  }
}

function applyTheme() {
  const theme = BOARD_THEMES[state.overlay.theme] ?? Object.values(BOARD_THEMES)[0]
  wrap.style.setProperty('--board-light', theme.light)
  wrap.style.setProperty('--board-dark', theme.dark)
  wrap.style.opacity = String(state.overlay.opacity)
}

export function overlayFrame(box = getContentBox()) {
  const o = state.overlay
  const sizePx = Math.max(36, o.size * box.height)
  const x = o.customized ? o.x : (box.width - sizePx) / 2 / (box.width || 1)
  return {
    left: box.left + x * box.width,
    top: box.top + o.y * box.height,
    size: sizePx,
    xFraction: x,
  }
}

function layout() {
  if (!wrap) return
  const o = state.overlay
  const beforeFirst = activeIndexAt(state.stamps, state.playback.time) < 0
  const hidden =
    !state.video.url || !o.visible || !state.pgn.moves.length || (beforeFirst && !o.showBefore)
  wrap.style.display = hidden ? 'none' : 'block'
  wrap.classList.toggle('split', o.mode === 'split')
  if (hidden) return

  if (o.mode === 'split') {
    // Board panel at the top of the 9:16 frame; placement is computed, not dragged.
    const frameBox = getFrameBox()
    const g = splitGeometry({
      frameW: frameBox.width,
      frameH: frameBox.height,
      share: o.split.share,
      videoW: state.video.width,
      videoH: state.video.height,
      pos: o.split.pos,
    })
    wrap.style.left = `${frameBox.left + g.boardX}px`
    wrap.style.top = `${frameBox.top + g.boardY}px`
    wrap.style.width = `${g.boardPx}px`
    wrap.style.height = `${g.boardPx}px`
    return
  }

  const frame = overlayFrame()
  wrap.style.left = `${frame.left}px`
  wrap.style.top = `${frame.top}px`
  wrap.style.width = `${frame.size}px`
  wrap.style.height = `${frame.size}px`
}

function wireDrag() {
  const bar = wrap.querySelector('.drag-bar')
  bar.addEventListener('pointerdown', (e) => {
    e.preventDefault()
    bar.setPointerCapture(e.pointerId)
    wrap.classList.add('dragging')
    const startX = e.clientX
    const startY = e.clientY
    const stageRect = stage.getBoundingClientRect()
    const rect = wrap.getBoundingClientRect()
    const origLeft = rect.left - stageRect.left
    const origTop = rect.top - stageRect.top
    let xf = state.overlay.x
    let yf = state.overlay.y

    const move = (ev) => {
      const box = getContentBox()
      if (!box.width || !box.height) return
      const left = origLeft + (ev.clientX - startX)
      const top = origTop + (ev.clientY - startY)
      xf = clamp((left - box.left) / box.width, -0.4, 0.96)
      yf = clamp((top - box.top) / box.height, -0.4, 0.96)
      wrap.style.left = `${box.left + xf * box.width}px`
      wrap.style.top = `${box.top + yf * box.height}px`
    }
    const up = () => {
      bar.removeEventListener('pointermove', move)
      bar.removeEventListener('pointerup', up)
      wrap.classList.remove('dragging')
      patchOverlay({ x: xf, y: yf, customized: true })
    }
    bar.addEventListener('pointermove', move)
    bar.addEventListener('pointerup', up)
  })
}

function wireResize() {
  const handle = wrap.querySelector('.resize-handle')
  handle.addEventListener('pointerdown', (e) => {
    e.preventDefault()
    e.stopPropagation()
    handle.setPointerCapture(e.pointerId)
    wrap.classList.add('dragging')
    const startX = e.clientX
    const startY = e.clientY
    const startSize = wrap.getBoundingClientRect().width
    let sizeFraction = state.overlay.size

    const move = (ev) => {
      const box = getContentBox()
      if (!box.height) return
      const delta = Math.max(ev.clientX - startX, ev.clientY - startY)
      const px = clamp(startSize + delta, 48, box.height * 1.2)
      sizeFraction = px / box.height
      wrap.style.width = `${px}px`
      wrap.style.height = `${px}px`
    }
    const up = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', up)
      wrap.classList.remove('dragging')
      patchOverlay({ size: sizeFraction, customized: true })
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', up)
  })
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}
